import React, { useState, useEffect } from 'react';

// Components
import Sidebar from '../../components/Sidebar';
import Main from './PurchaseOrder/Main';
import Form from './PurchaseOrder/Form';

import {
  getDataWithComp,
  getDocStatusColour,
  getAlert,
  getMaxDocNo,
  getClearLocalStorage,
  updateData,
} from '../../../utils/SamuiUtils';

import { formatThaiDateUiToDate, getCreateDateTime } from '../../../utils/DateUtils';

import { Box } from "@mui/material";

import { getReport } from '../../../utils/ReportUtils';

const resolveStatusCode = (status) => {
  if (!status || typeof status !== 'object') {
    return undefined;
  }

  const valueKeys = ['DocSetStatus', 'DocSet_Status', 'DocSetstatus', 'Doc_Status', 'DocStatus'];

  for (const key of valueKeys) {
    if (status[key] !== undefined && status[key] !== null) {
      const numeric = Number(status[key]);
      if (!Number.isNaN(numeric)) {
        return numeric;
      }
    }
  }

  if (status.DocSetStaus_Value !== undefined && status.DocSetStaus_Value !== null) {
    const numeric = Number(status.DocSetStaus_Value);
    if (!Number.isNaN(numeric)) {
      return numeric;
    }
  }

  return undefined;
};

const getStatusProp = (status, keys) => {
  for (const key of keys) {
    if (status[key] !== undefined && status[key] !== null) {
      return status[key];
    }
  }
  return undefined;
};

const normalizeStatus = (status) => {
  if (!status) {
    return null;
  }

  const label = getStatusProp(status, [
    'DocSetStaus_Name',
    'DocSetStatus_Name',
    'Doc_Status_Name',
    'DocStatus_Name',
    'DocSetStausName',
    'DocStatusName',
  ]);

  const value = resolveStatusCode(status);

  if (!label || value === undefined) {
    return null;
  }

  const color = getStatusProp(status, [
    'DocSetColour',
    'DocSetStatus_Colour',
    'DocStatus_Colour',
    'Doc_Status_Colour',
    'DocSetStaus_Colour',
  ]) || '#6c757d';

  return { label, value, color };
};

function PurchaseOrder() {
  const [mode, setMode] = useState('');
  const [dataMasterList, setDataMasterList] = useState([]);
  const [dataDetailList, setDataDetailList] = useState([]);
  const [statusColours, setStatusColours] = useState([]);
  const [statusPaidColours, setStatusPaidColours] = useState([]);
  const [statusReceiveColours, setStatusReceiveColours] = useState([]);
  const [maxDocNo, setMaxDocNo] = useState();
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusCodeLookup, setStatusCodeLookup] = useState({});

  useEffect(() => {
    initialize();
  }, []);

  const fetchRealtime = async () => {
    try {
      const masterList = await getDataWithComp('API_0201_PO_H', 'ORDER BY Doc_No DESC');
      const docStatusColour = await getDocStatusColour('PO', 'Doc_Status');
      const docStatusPaidColour = await getDocStatusColour('PO', 'Doc_Status_Paid');
      const docStatusReceiveColour = await getDocStatusColour('PO', 'Doc_Status_Receive');

      if (masterList && masterList.length > 0) {
        const sortedData = masterList.sort((a, b) => a.Doc_Id - b.Doc_Id);
        setDataMasterList(sortedData);
      }

      const allowedStatusDefinitions = [
        { label: 'รออนุมัติ', fallbackValue: 1, fallbackColor: '#F59E0B' },
        { label: 'อนุมัติ', fallbackValue: 2, fallbackColor: '#22C55E' },
        { label: 'ไม่อนุมัติ', fallbackValue: 3, fallbackColor: '#EF4444' },
        // ลบสถานะ 'ปิดงาน' ออกจากตัวเลือก
        { label: 'ยกเลิก', fallbackValue: 13, fallbackColor: '#000000' },
      ];

      if (Array.isArray(docStatusColour) && docStatusColour.length > 0) {
        setStatusColours(docStatusColour);

        const normalizedStatuses = docStatusColour
          .map(normalizeStatus)
          .filter(Boolean);

        const statusMap = new Map(
          normalizedStatuses.map((status) => [status.label, status])
        );

        const approvalStatuses = allowedStatusDefinitions.map(
          ({ label, fallbackValue, fallbackColor }) => {
            const match = statusMap.get(label);
            return {
              label,
              value: match?.value ?? fallbackValue,
              color: match?.color ?? fallbackColor,
            };
          }
        );

        setStatusOptions(approvalStatuses);
        setStatusCodeLookup(
          approvalStatuses.reduce((acc, status) => {
            acc[status.label] = status.value;
            return acc;
          }, {})
        );
      } else {
        const fallbackStatuses = allowedStatusDefinitions.map(
          ({ label, fallbackValue, fallbackColor }) => ({
            label,
            value: fallbackValue,
            color: fallbackColor,
          })
        );
        setStatusColours([]);
        setStatusOptions(fallbackStatuses);
        setStatusCodeLookup(
          fallbackStatuses.reduce((acc, status) => {
            acc[status.label] = status.value;
            return acc;
          }, {})
        );
      }

      if (docStatusPaidColour && docStatusPaidColour.length > 0) {
        setStatusPaidColours(docStatusPaidColour);
      }

      if (docStatusReceiveColour && docStatusReceiveColour.length > 0) {
        setStatusReceiveColours(docStatusReceiveColour);
      }

      // หาค่าสูงของ DocNo ใน PO_H
      const findMaxDocNo = await getDataWithComp('PO_H', 'ORDER BY Doc_No DESC');
      const maxDoc = getMaxDocNo(findMaxDocNo, 'PO');
      setMaxDocNo(maxDoc);
    } catch (error) {
      getAlert('FAILED', error.message);
    }
  };

  const initialize = async () => {
    try {
      await getClearLocalStorage();
      setMode('S');
      fetchRealtime();
    } catch (error) {
      getAlert('FAILED', error.message);
    }
  };

  const onPageInsert = () => {
    setMode('I')
  };

  const onRowSelected = (docNo) => {
    setMaxDocNo(docNo);
    setMode('U');
  };

  const handleStatusChange = async (docNo, nextStatusValue, rowData) => {
    if (!docNo || nextStatusValue === undefined || nextStatusValue === null) {
      return;
    }

    const numericStatus = Number(nextStatusValue);
    if (Number.isNaN(numericStatus)) {
      return;
    }

    const currentStatus = rowData?.Doc_Status ?? rowData?.docStatus;
    if (currentStatus !== undefined && String(currentStatus) === String(numericStatus)) {
      return;
    }

    const targetOption = statusOptions.find(
      (option) => String(option.value) === String(numericStatus)
    );

    if (!targetOption) {
      getAlert('FAILED', 'ไม่พบข้อมูลสถานะที่ต้องการอัปเดต');
      return;
    }

    try {
      const now = getCreateDateTime();
      const payload = {
        Doc_Status: numericStatus,
        Approved_Date: formatThaiDateUiToDate(now),
        Approved_By_Name: window.localStorage.getItem('name'),
        Approved_By_Id: window.localStorage.getItem('emp_id'),
        Approved_Memo: rowData?.Approved_Memo || null,
        Update_Date: formatThaiDateUiToDate(now),
        Update_By_Name: window.localStorage.getItem('name'),
        Update_By_Id: window.localStorage.getItem('emp_id'),
      };

      await updateData('PO_H', payload, {
        Doc_No: docNo,
        Comp_Id: window.localStorage.getItem('company'),
      });

      await fetchRealtime();
    } catch (error) {
      getAlert('FAILED', error?.response?.data?.message || error.message);
    }
  };

  const isStatusEditable = (rowData) => {
    if (!rowData) {
      return false;
    }

    const currentStatus = rowData.Doc_Status ?? rowData.docStatus;
    const cancelCode = statusCodeLookup['ยกเลิก'];

    if (cancelCode !== undefined && Number(currentStatus) === Number(cancelCode)) {
      return false;
    }

    const receiveStatusRaw =
      rowData.Doc_Status_ReceiveName ||
      rowData.docStatusReceiveName ||
      rowData.Doc_Status_Receive;

    if (receiveStatusRaw !== undefined && receiveStatusRaw !== null) {
      if (typeof receiveStatusRaw === 'string') {
        const normalizedReceive = receiveStatusRaw.trim();
        if (normalizedReceive !== 'รอรับสินค้า') {
          return false;
        }
      } else {
        const numericReceive = Number(receiveStatusRaw);
        if (!Number.isNaN(numericReceive) && numericReceive !== 1) {
          return false;
        }
      }
    }

    return true;
  };

  return (
    <Box className="PurchaseOrder" sx={{ display: 'flex' }}>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, ml: 'var(--sidebar-width, 220px)', p: 3 }}>
        {mode === 'S' ? (
          <Main
            masterList={dataMasterList}
            detailList={dataDetailList}
            statusColours={statusColours}
            statusPaidColours={statusPaidColours}
            statusReceiveColours={statusReceiveColours}
            name={'ใบสั่งซื้อ'}
            onPageInsert={onPageInsert}
            onRowSelected={onRowSelected}
            getReport={getReport}
            statusOptions={statusOptions}
            onStatusChange={handleStatusChange}
            isStatusEditable={isStatusEditable}
          />
        ) : (
          <Form
            callInitialize={initialize}
            mode={mode}
            setMode={setMode}
            name={'ใบสั่งซื้อ'}
            maxDocNo={maxDocNo}
            getReport={getReport}
          />
        )}
      </Box>
    </Box>
  );
}

export default PurchaseOrder;
